/**
 * FeedbackTypeSelector/index.tsx — 8208-A
 *
 * Floating dropdown panel that appears when the chevron on FeedbackTabLabel is clicked.
 * Shows 4 feedback categories in an accordion layout. Only one category expanded at a time.
 * Click-outside and Escape key close the dropdown.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import SVG from "react-inlinesvg";
import Auth from "../../../Auth";
import useEditPluginInstallations from "../../../hooks/useEditPluginInstallations";
import { FEEDBACK_CATEGORIES, FeedbackPlugin, getPluginsByCategory, FEEDBACK_CATEGORY_KEYS } from "../../../shared/feedbackCategories";
import { CategoryRow } from "./CategoryRow";
import { CustomPluginRow } from "./CustomPluginRow";
import { CustomTypeModal } from "./CustomTypeModal";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor";
import { AnchorIds } from "../../StickyTour/TourTypes";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../sudokit/components/Dialog";
import { SudoButton } from "../../../sudokit/components/SudoButton";
import {
  applyFeedbackPreferencesToInstallations,
  normalizeFeedbackPreferences,
  setFeedbackPreferencePluginEnabled,
} from "../feedbackProjectPreferences";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_EXPANDED_CATEGORY = "developmental";

function isLastEnabledPluginInCategory(plugin: FeedbackPlugin | undefined, plugins: FeedbackPlugin[]): boolean {
  if (!plugin?.enabled) return false;
  return plugins.filter((item) => item.category === plugin.category && item.enabled).length <= 1;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeedbackTypeSelectorProps {
  isOpen: boolean;
  selectedCategory?: string | null;
  project?: {
    id?: string;
    data?: {
      feedbackPreferences?: unknown;
    };
    update?: (data: { feedbackPreferences: unknown }) => Promise<unknown>;
  } | null;
  onClose: () => void;
  onSelectCategory?: (categoryKey: string) => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const SelectorPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 30000;
  background: var(--menu-color);
  border-radius: 16px;
  box-shadow: 0px 1.66px 1.66px rgba(0, 0, 0, 0.15),
    0px 0px 12.46px rgba(0, 0, 0, 0.15);
  padding: 12px 0;
  width: 416px;
  overflow-y: auto;
  box-sizing: border-box;
`;

const VIEWPORT_BOTTOM_GAP = 40;

const SelectorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 12px;
`;

const CreateNewButton = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 4px;
  color: var(--primary-cta);
  cursor: pointer;
  user-select: none;
  border-radius: 8px;

  svg,
  img {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  svg path {
    fill: currentColor;
  }

  &:hover {
    background-color: var(--menu-color-hover);
  }
`;

const CreateNewText = styled.span`
  font-size: 16px;
  font-weight: 500;
  padding-left: 4px;
`;

const CustomTypesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedbackTypeSelector({
  isOpen,
  selectedCategory,
  project,
  onClose,
  onSelectCategory,
}: FeedbackTypeSelectorProps): JSX.Element | null {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pendingDeletePlugin, setPendingDeletePlugin] = useState<FeedbackPlugin | null>(null);
  const [draftFeedbackPreferences, setDraftFeedbackPreferences] = useState<unknown>(null);
  const draftFeedbackPreferencesRef = useRef<unknown>(null);
  const latestFeedbackPreferencesSaveVersionRef = useRef(0);

  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const setLocalDraftFeedbackPreferences = useCallback((preferences: unknown) => {
    draftFeedbackPreferencesRef.current = preferences;
    setDraftFeedbackPreferences(preferences);
  }, []);

  useEffect(() => {
    setLocalDraftFeedbackPreferences(null);
    latestFeedbackPreferencesSaveVersionRef.current += 1;
  }, [project?.id, setLocalDraftFeedbackPreferences]);

  // Sync accordion to selection when menu opens. No category selected means no
  // category expanded; stale accordion-only state should not imply a selection.
  useEffect(() => {
    if (!isOpen) return;

    if (!selectedCategory) {
      setExpandedCategory(null);
      return;
    }

    const isSystemCategory = FEEDBACK_CATEGORIES.some(c => c.key === selectedCategory);
    if (isSystemCategory) {
      setExpandedCategory(selectedCategory);
    } else {
      setExpandedCategory(null);
    }
  }, [isOpen, selectedCategory]);

  const { installations, refetch } = useEditPluginInstallations();
  const projectFeedbackPreferences = draftFeedbackPreferences ?? project?.data?.feedbackPreferences ?? null;

  // When the selected type is a user custom plugin by name, reset to default if it
  // was removed (e.g. deleted in chat → refresh_swr → installations no longer lists it).
  useEffect(() => {
    if (!onSelectCategory || !selectedCategory) return;
    if (FEEDBACK_CATEGORY_KEYS.has(selectedCategory)) return;
    if (installations === undefined) return;

    const stillExists = (installations as FeedbackPlugin[]).some(
      (p) => p.type === "USER" && p.name === selectedCategory,
    );
    if (!stillExists) {
      onSelectCategory(DEFAULT_EXPANDED_CATEGORY);
    }
  }, [installations, selectedCategory, onSelectCategory]);

  // Group plugins by category
  const effectiveInstallations = applyFeedbackPreferencesToInstallations(
    (installations as FeedbackPlugin[] | undefined) ?? [],
    projectFeedbackPreferences,
  );
  const pluginsByCategory = getPluginsByCategory(
    (effectiveInstallations as FeedbackPlugin[] | undefined) ?? []
  );

  // -------------------------------------------------------------------------
  // Toggle accordion category
  // -------------------------------------------------------------------------

  const handleToggleExpand = (categoryKey: string) => {
    setExpandedCategory((prev) => {
      const next = prev === categoryKey ? null : categoryKey;
      return next;
    });
  };

  // -------------------------------------------------------------------------
  // Plugin enable toggle (optimistic update pattern from SuggestionTypes.js)
  // -------------------------------------------------------------------------

  const handlePluginEnabledToggle = async (pluginId: string, currentEnabled: boolean) => {
    const newEnabled = !currentEnabled;

    if (project?.update) {
      const latestPreferences = draftFeedbackPreferencesRef.current ?? project?.data?.feedbackPreferences ?? null;
      const latestEffectiveInstallations = applyFeedbackPreferencesToInstallations(
        (installations as FeedbackPlugin[] | undefined) ?? [],
        latestPreferences,
      );
      const plugin = (latestEffectiveInstallations as FeedbackPlugin[] | undefined)?.find((item) => item.id === pluginId);
      if (!plugin) return;
      if (isLastEnabledPluginInCategory(plugin, latestEffectiveInstallations as FeedbackPlugin[])) {
        return;
      }

      const nextPreferences = setFeedbackPreferencePluginEnabled(
        {
          ...normalizeFeedbackPreferences(latestPreferences),
          ...(selectedCategory
            ? { selectedCategory, hasUserSelectedCategory: true }
            : {}),
        },
        plugin,
        !plugin.enabled,
      );
      const saveVersion = latestFeedbackPreferencesSaveVersionRef.current + 1;
      latestFeedbackPreferencesSaveVersionRef.current = saveVersion;

      setLocalDraftFeedbackPreferences(nextPreferences);
      try {
        const result = await project.update({ feedbackPreferences: nextPreferences });
        if (!result && latestFeedbackPreferencesSaveVersionRef.current === saveVersion) {
          setLocalDraftFeedbackPreferences(null);
        }
      } catch {
        if (latestFeedbackPreferencesSaveVersionRef.current === saveVersion) {
          setLocalDraftFeedbackPreferences(null);
        }
      }
      return;
    }

    const currentPlugin = (installations as FeedbackPlugin[] | undefined)?.find((plugin) => plugin.id === pluginId);
    if (isLastEnabledPluginInCategory(currentPlugin, (installations as FeedbackPlugin[] | undefined) ?? [])) {
      return;
    }

    // Optimistically update the installations data
    const optimisticUpdate = (installations as FeedbackPlugin[] | undefined)?.map((plugin) =>
      plugin.id === pluginId ? { ...plugin, enabled: newEnabled } : plugin
    );

    // Update the UI immediately
    refetch(optimisticUpdate, false);

    try {
      await Auth.fetchWithIdToken(`/api/edit-plugins/${pluginId}/enable`, {
        method: "POST",
        enabled: newEnabled,
      });

      // Revalidate to ensure we have the latest server state
      await refetch();
    } catch {
      // Revert optimistic update on error
      refetch(installations, false);
    }
  };

  // -------------------------------------------------------------------------
  // Custom plugin delete (uninstall)
  // -------------------------------------------------------------------------

  const handleCustomPluginDelete = (pluginId: string) => {
    const pluginToDelete = (installations as FeedbackPlugin[] | undefined)?.find(
      (p) => p.id === pluginId
    );
    setPendingDeletePlugin(pluginToDelete ?? null);
  };

  const handleConfirmCustomPluginDelete = async () => {
    if (!pendingDeletePlugin) return;
    const pluginId = pendingDeletePlugin.id;
    setPendingDeletePlugin(null);
    // Optimistically remove from installations
    const optimisticUpdate = (installations as FeedbackPlugin[] | undefined)?.filter(
      (plugin) => plugin.id !== pluginId
    );
    refetch(optimisticUpdate, false);

    try {
      await Auth.fetchWithIdToken(`/api/edit-plugins/${pluginId}/uninstall`, {
        method: "POST",
      });
      await refetch();
    } catch {
      // Revert optimistic update on error
      refetch(installations, false);
    }
  };

  // -------------------------------------------------------------------------
  // Custom type modal state (8212-A)
  // -------------------------------------------------------------------------

  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [editingPlugin, setEditingPlugin] = useState<{
    id: string;
    name: string;
    content: string;
  } | null>(null);

  const handleCustomPluginEdit = (pluginId: string) => {
    const plugin = (installations as FeedbackPlugin[] | undefined)?.find(
      (p) => p.id === pluginId
    );
    if (!plugin) return;
    setEditingPlugin({
      id: plugin.id,
      name: plugin.name ?? "",
      content: (plugin.content as string) ?? "",
    });
    setIsCustomModalOpen(true);
    onClose();
  };

  const handleCreateNew = () => {
    setEditingPlugin(null);
    setIsCustomModalOpen(true);
    onClose();
  };

  const handleCustomModalClose = useCallback(() => {
    setIsCustomModalOpen(false);
    setEditingPlugin(null);
  }, []);

  const handleCustomModalSave = useCallback(
    async (name: string, instructions: string) => {
      try {
        if (editingPlugin) {
          // Update existing plugin (PUT /api/edit-plugins/:id)
          await Auth.fetchWithIdToken(`/api/edit-plugins/${editingPlugin.id}`, {
            method: "PUT",
            name,
            content: instructions,
          });
        } else {
          // Create new plugin (POST /api/edit-plugins)
          await Auth.fetchWithIdToken("/api/edit-plugins", {
            method: "POST",
            name,
            content: instructions,
            enabled: true,
          });
        }
        // Close modal and refresh the installations list
        setIsCustomModalOpen(false);
        setEditingPlugin(null);
        await refetch();
        // Auto-select the newly created/edited custom type
        onSelectCategory?.(name);
      } catch {
        // Error handling — keep modal open so user can retry
      }
    },
    [editingPlugin, refetch, onSelectCategory]
  );

  // -------------------------------------------------------------------------
  // Adapt max-height to the viewport so the panel grows to fill available
  // vertical space, leaving VIEWPORT_BOTTOM_GAP px of room at the bottom.
  // Recomputes on window resize.
  // -------------------------------------------------------------------------

  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = dropdownRef.current;
    if (!el) return;

    function updateMaxHeight() {
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      el.style.maxHeight = `${window.innerHeight - top - VIEWPORT_BOTTOM_GAP}px`;
    }

    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => {
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [isOpen]);

  // -------------------------------------------------------------------------
  // Click-outside to close (pattern from Menu/index.js lines 508-529)
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      // Ignore clicks inside react-modal portals (rendered outside dropdownRef in DOM)
      if ((target as Element).closest?.(".ReactModal__Overlay")) {
        return;
      }
      // Ignore clicks inside the delete confirmation dialog. It is portaled
      // outside the selector, so otherwise pointerdown closes the selector
      // before the dialog button click can run.
      if ((target as Element).closest?.(".feedback-delete-dialog")) {
        return;
      }
      // Ignore clicks inside sticky-tour stickies — they're portaled outside
      // the dropdown but are part of the guided flow, and closing on mousedown
      // would unmount their buttons before mouseup, swallowing the click.
      if ((target as Element).closest?.(".sticky-tour-sticky")) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        // Only skip close for clicks on the chevron itself — not the entire anchor
        if ((target as Element).closest?.(".chevron")) {
          return;
        }
        onClose();
      }
    }

    // pointerdown fires reliably in cases where bubble-phase mousedown is suppressed (e.g.
    // when other popover layers intercept the event); keep mousedown for non-pointer
    // environments.
    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // -------------------------------------------------------------------------
  // Escape key to close
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // -------------------------------------------------------------------------
  // Close when the window loses focus (app switch). Editor clicks are covered
  // by the outside-click handler above since they bubble in the main document.
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return;

    function handleWindowBlur() {
      onClose();
    }

    window.addEventListener("blur", handleWindowBlur);
    return () => {
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [isOpen, onClose]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const customPlugins = pluginsByCategory["custom"] ?? [];

  return (
    <>
    {isOpen && (
      <SelectorPanel className="feedback-type-selector" ref={dropdownRef}>
        <StickyNoteAnchor anchorId={AnchorIds.FEEDBACK_TYPE_DROPDOWN} />
        <SelectorList className="feedback-type-selector-list">
          <CreateNewButton
            className="create-new-button"
            role="button"
            tabIndex={0}
            onClick={handleCreateNew}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCreateNew();
              }
            }}
            aria-label="Add custom feedback type"
          >
            <SVG src="/assets/add.svg" aria-hidden="true" />
            <CreateNewText>Add Custom Type</CreateNewText>
          </CreateNewButton>
          {FEEDBACK_CATEGORIES.map((category) => (
            <CategoryRow
              key={category.key}
              category={category}
              plugins={pluginsByCategory[category.key] ?? []}
              isExpanded={expandedCategory === category.key}
              isSelected={selectedCategory === category.key}
              onToggleExpand={() => handleToggleExpand(category.key)}
              onPluginToggle={handlePluginEnabledToggle}
              onSelect={(key) => onSelectCategory?.(key)}
            />
          ))}
          {customPlugins.length > 0 && (
            <CustomTypesSection>
              {customPlugins.map((plugin) => (
                <CustomPluginRow
                  key={plugin.id}
                  plugin={plugin}
                  isSelected={selectedCategory === plugin.name}
                  onDelete={handleCustomPluginDelete}
                  onEdit={handleCustomPluginEdit}
                  onSelect={(categoryKey) => {
                    setExpandedCategory(null);
                    onSelectCategory?.(categoryKey);
                  }}
                />
              ))}
            </CustomTypesSection>
          )}
        </SelectorList>
      </SelectorPanel>
    )}
    <CustomTypeModal
      isOpen={isCustomModalOpen}
      onClose={handleCustomModalClose}
      onSave={handleCustomModalSave}
      initialName={editingPlugin?.name}
      initialInstructions={editingPlugin?.content}
      pluginId={editingPlugin?.id}
    />
    <Dialog
      open={!!pendingDeletePlugin}
      onOpenChange={(open: boolean) => {
        if (!open) {
          setPendingDeletePlugin(null);
        }
      }}
    >
      <DialogContent className="feedback-delete-dialog">
        <DialogHeader>
          <DialogTitle>Remove custom type?</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove "{pendingDeletePlugin?.name ?? "this custom type"}" from your list?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <SudoButton
            variant="tertiaryGrey"
            onClick={() => setPendingDeletePlugin(null)}
          >
            Cancel
          </SudoButton>
          <SudoButton
            variant="danger"
            onClick={handleConfirmCustomPluginDelete}
          >
            Remove
          </SudoButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default FeedbackTypeSelector;
